<template>
<div @mouseenter="onMouseenter" class="page-viewer" :style="viewerStyle">
    <div @scroll="onScroll" class="scroll-body">
        <ViewerHeader></ViewerHeader>
        <GuanzoMessage />
        <Toolbar/>
        <MessageToViewers />
        <ErrorMessage v-if="err">
            <button @click="fetchSuggestions" class="button is-small m-t-30">
                Retry
            </button>
        </ErrorMessage>
        <SuggestionList
            v-else
            :suggestions="masterList"
            :lastScrollToBottomDate="lastScrollToBottomDate" />
    </div>
    <ViewerFooter/>
    <EmotePicker
        :channelId="channelId"
        :lastUserActivityDate="lastUserActivityDate"/>
    <!-- Every single modal in the app is rendered here -->
    <PortalTarget name="modal" slim />
    <PortalTarget name="canvas" slim />
    <FontLoader :fontConfig="fontConfig" :styleObj.sync="viewerStyle" />
    <IntroTutorial/>
</div>
</template>

<script>
// TODO: This component has too many responsibilities.

import { mapState, mapGetters } from 'vuex'

import * as _ from '@/lodash'
import * as M from '@/store/mutations'
import ViewerHeader from '@/components/viewer/header/ViewerHeader'
import ViewerFooter from '@/components/viewer/footer/ViewerFooter'
import SuggestionList from '@/components/viewer/SuggestionList'
import IntroTutorial from '@/components/viewer/IntroTutorial'
import Toolbar from '@/components/viewer/Toolbar'
import EmotePicker from '@/components/emotes/EmotePicker'
import FontLoader from '@/font-loader/FontLoader'
import MessageToViewers from '@/components/MessageToViewers'
import GuanzoMessage from '@/components/GuanzoMessage'
// import * as utils from '@/utils'

export default {
    name: 'PageViewer',
    data: () => ({
        err: null,
        lastUserActivityDate: null,
        lastScrollToBottomDate: null,
        viewerStyle: {},
    }),
    computed: {
        ...mapState(['channel', 'showIntroTutorial']),
        ...mapGetters([
            'channelId',
            'masterList',
            'suggestionList',
            'isAllowedToSuggest',
            'lastSuggestionDate',
            'listFilteringProps',
        ]),
        fontConfig: vm => {
            const mutation = M.SET_FONT_LOADING_STATE
            return {
                fontUrl: vm.channel.fontUrl,
                mutation,
                channelId: vm.channelId,
                fontKey: `${vm.channelId}-${mutation}`
            }
        },
    },
    watch: {
        // TODO: When jumping channels, this watcher gets called twice.
        // Once, when channelId changes, and again, if the channel has
        // a non-default listType. Maybe, stop making requests purely
        // data driven? Or find a more deterministic algo to know when
        // to fetch suggestions.
        async listFilteringProps () {
            if (!this.listFilteringProps.listType) {
                return
            }
            await this.fetchSuggestions()

            this.$store.commit(M.INCREMENT_LIST_RENDER_COUNT)
        },
        lastSuggestionDate: 'startClockTick',
    },
    methods: {
        // Slight hack... There's a few properties that trigger a fetch,
        // and these props are set independently. To avoid triggering
        // multiple fetches at the same time, introduce a throttle.
        fetchSuggestions: _.throttle(async function () {
            try {
                this.err = null
                await this.$store.dispatch('fetchSuggestions')
            } catch (err) {
                this.err = err
                Sentry.captureException(err)
            }
        }, 350),
        onMouseenter () {
            this.lastUserActivityDate = new Date()
        },
        onScroll: _.throttle(function (event) {
            const $el = event.target
            const bottomHeight = $el.scrollHeight - $el.offsetHeight
            const padding = 35

            if ($el.scrollTop >= (bottomHeight - padding)) {
                this.lastScrollToBottomDate = new Date()
            }
        }, 100),
        // forces isAllowedToSuggest getter to update
        startClockTick () {
            clearInterval(this.intervalId)
            this.intervalId = setInterval(() => {
                this.$store.commit(M.SET_NOW_TIME, { now: Date.now() })

                if (this.isAllowedToSuggest) {
                    clearInterval(this.intervalId)
                }
            }, 1000)
        }
    },
    components: {
        ViewerHeader,
        ViewerFooter,
        SuggestionList,
        EmotePicker,
        FontLoader,
        IntroTutorial,
        MessageToViewers,
        GuanzoMessage,
        Toolbar,
    },
}
</script>

<style lang="scss">
#app {
    &.is-mobile,
    &.is-popout-window,
    &.is-video-component-window {
        .page-viewer {
            height: 100vh;
            width: auto;
        }
    }
    &.is-popout-window .page-viewer {
        max-width: 500px;
    }
}
</style>

<style lang="scss" scoped>
.page-viewer {
    display: flex;
    flex-direction: column;
    position: relative;
    // Default dimensions is for a panel component.
    height: $sbox-panel-height;
    width: $sbox-panel-width;
    max-height: 100vh;
    overflow-y: hidden;
    overflow-x: hidden;
}

.scroll-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow-y: scroll;
    overflow-x: hidden;
}
</style>
